import React, { useMemo, useState } from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel,
  Box, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DriveFile } from '../services/myDriveService';
import { ViewMode } from './Toolbar';
import FileItem from './FileItem';
import { mimeLabel } from '../utils/mimeLabel';

// Sprint 4-post-c · 欄位排序（client-side·資料夾 always top）
export type SortKey = 'filename' | 'size_bytes' | 'mime_type' | 'updated_at' | 'scan_status';
export type SortDir = 'asc' | 'desc';

export function sortDriveFiles(rows: DriveFile[], key: SortKey, dir: SortDir): DriveFile[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    // 資料夾 always top（一般檔案總管 UX）
    if (a.is_directory && !b.is_directory) return -1;
    if (!a.is_directory && b.is_directory) return 1;
    let cmp = 0;
    if (key === 'size_bytes') cmp = (a.size_bytes ?? 0) - (b.size_bytes ?? 0);
    else if (key === 'updated_at') {
      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      cmp = ta - tb;
    } else if (key === 'mime_type') {
      cmp = mimeLabel(a.mime_type).localeCompare(mimeLabel(b.mime_type), 'zh-TW');
    } else {
      cmp = String(a[key] ?? '').localeCompare(String(b[key] ?? ''), 'zh-TW');
    }
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

interface Props {
  files: DriveFile[];
  loading: boolean;
  view: ViewMode;
  selected: Set<number>;
  readonly: boolean;
  onItemClick: (id: number, ev: React.MouseEvent) => void;
  onDoubleClick: (file: DriveFile) => void;
  onContextMenu: (ev: React.MouseEvent, file: DriveFile) => void;
  onPreview: (file: DriveFile) => void;
  onDownload: (file: DriveFile) => void;
  onDelete: (file: DriveFile) => void;
  onDragStart: (ev: React.DragEvent, file: DriveFile) => void;
  onDragOver: (ev: React.DragEvent) => void;
  onDrop: (ev: React.DragEvent, target: DriveFile | null) => void;
}

export default function FileBrowser({
  files, loading, view, selected, readonly,
  onItemClick, onDoubleClick, onContextMenu, onPreview, onDownload, onDelete,
  onDragStart, onDragOver, onDrop,
}: Props) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('filename');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortedFiles = useMemo(() => sortDriveFiles(files, sortKey, sortDir), [files, sortKey, sortDir]);

  const SortHeader = ({ colKey, label, align }: { colKey: SortKey; label: string; align?: 'right' }) => (
    <TableCell align={align}>
      <TableSortLabel active={sortKey === colKey} direction={sortKey === colKey ? sortDir : 'asc'}
        onClick={() => handleSort(colKey)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (!loading && files.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('myDrive.empty', '（無檔案）')}</Typography>
      </Box>
    );
  }

  if (view === 'grid') {
    return (
      <Box
        sx={{ flex: 1, overflow: 'auto', p: 1, display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start' }}
        onDragOver={onDragOver}
        onDrop={e => onDrop(e, null)}
      >
        {sortedFiles.map(file => (
          <FileItem
            key={file.id}
            file={file}
            selected={selected.has(file.id)}
            readonly={readonly}
            view="grid"
            onSingleClick={ev => onItemClick(file.id, ev)}
            onDoubleClick={() => onDoubleClick(file)}
            onContextMenu={ev => onContextMenu(ev, file)}
            onPreview={() => onPreview(file)}
            onDownload={() => onDownload(file)}
            onDelete={() => onDelete(file)}
            onDragStart={ev => onDragStart(ev, file)}
            onDragOver={onDragOver}
            onDrop={ev => onDrop(ev, file.is_directory ? file : null)}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: 'auto' }} onDragOver={onDragOver} onDrop={e => onDrop(e, null)}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 32 }} />
            <SortHeader colKey="filename" label={t('myDrive.filename', '檔名')} />
            <SortHeader colKey="size_bytes" label={t('myDrive.size', '大小')} />
            <SortHeader colKey="mime_type" label={t('myDrive.type', '類型')} />
            <SortHeader colKey="updated_at" label={t('myDrive.updatedAt', '最後儲存')} />
            <SortHeader colKey="scan_status" label={t('myDrive.scanStatus', '掃描')} />
            <TableCell align="right">{t('myDrive.actions', '操作')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedFiles.map(file => (
            <FileItem
              key={file.id}
              file={file}
              selected={selected.has(file.id)}
              readonly={readonly}
              view="list"
              onSingleClick={ev => onItemClick(file.id, ev)}
              onDoubleClick={() => onDoubleClick(file)}
              onContextMenu={ev => onContextMenu(ev, file)}
              onPreview={() => onPreview(file)}
              onDownload={() => onDownload(file)}
              onDelete={() => onDelete(file)}
              onDragStart={ev => onDragStart(ev, file)}
              onDragOver={onDragOver}
              onDrop={ev => onDrop(ev, file.is_directory ? file : null)}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
